// ── Shared pieces for the journal, the Today board and the effort panel ─────────
//
// Tech Mode part 2 (2 Oct). Formatting and the small building blocks the three
// screens share, so a ticket tag or an event reads the same on all of them.

import React, { createContext, useContext } from 'react';

// When My Workbench has a background, the journal and Today panels use the
// same frosted glass as the Board's toolbar (3 Oct). TechView provides the
// glass style; null means the plain card. The effort panel in Ticket Detail
// never has one.
export const SurfaceContext = createContext(null);
const surface = (T, glass) => glass
  ? { background: glass.background, border: glass.border, boxShadow: glass.boxShadow, borderRadius: 14 }
  : { background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12 };

export const DISPLAY_FONT = "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif";

// 8100 -> "2h 15m". Under an hour still shows "0h 24m", so columns line up.
export function fmtDur(seconds) {
  const mins = Math.max(0, Math.floor((Number(seconds) || 0) / 60));
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

export const fmtMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
export const fmtRate = (n) => `$${Math.round(Number(n) || 0)}/hr`;
export const fmtTime = (d) => new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
export const fmtWhen = (d) => new Date(d).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// A shop day "2026-10-02" as a heading. Built from the parts, not new Date(str),
// which would read it as UTC midnight and show the day before in New Jersey.
export function dayHeading(date, today) {
  const [y, m, d] = String(date).split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const label = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return date === today ? `Today, ${label}` : label;
}

// One ticket event in words. `by` is set when someone else did it (a send-back
// in a tech's journal).
export function eventLabel(e) {
  const d = e.detail || {};
  switch (e.kind) {
    case 'status': return d.to ? `Moved to ${d.to}` : 'Status changed';
    case 'sendback': return `Sent back${d.reason ? `: ${d.reason}` : ''}${e.by ? ` (${e.by})` : ''}`;
    case 'reassign': return d.toName ? `Reassigned to ${d.toName}` : 'Reassigned';
    case 'checklist': return `Checklist ✓${d.template ? ` ${d.template}` : ''}${d.attention ? ` · ${d.attention} need${d.attention === 1 ? 's' : ''} attention` : ''}`;
    case 'completed': return 'Done on My Workbench';
    case 'reopened': return 'Back in progress';
    default: return e.kind;
  }
}

export function colors(isDark) {
  return {
    row: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    chip: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    sub: isDark ? '#8a98b5' : '#666',
    good: isDark ? '#2ecc71' : '#1e8449',
    warn: '#e67e22',
    bad: isDark ? '#ff8a7a' : '#c0392b',
  };
}

export function Panel({ T, title, right, children, style }) {
  const glass = useContext(SurfaceContext);
  return (
    <div style={{ ...surface(T, glass), padding: 16,
      display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0, ...style }}>
      {(title || right) && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <SectionLabel T={T}>{title}</SectionLabel>
          {right && <div style={{ marginLeft: 'auto', fontSize: 13, color: T.textSub }}>{right}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function SectionLabel({ T, children }) {
  return <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.textSub }}>{children}</div>;
}

export function IdTag({ id }) {
  return <span style={{ fontSize: 12, fontWeight: 700, color: '#1A2535', background: '#E67E22', borderRadius: 5, padding: '2px 7px', whiteSpace: 'nowrap' }}>SVC-{id}</span>;
}

export function Chip({ isDark, color, dot, children }) {
  const c = colors(isDark);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '3px 9px', borderRadius: 999,
      background: c.chip, color: color || c.sub, whiteSpace: 'nowrap' }}>
      {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot }} />}
      {children}
    </span>
  );
}

export function StatTile({ T, label, value, sub, valueColor }) {
  const glass = useContext(SurfaceContext);
  return (
    <div style={{ ...surface(T, glass), padding: 16, minWidth: 0 }}>
      <SectionLabel T={T}>{label}</SectionLabel>
      <div style={{ fontFamily: DISPLAY_FONT, fontSize: 30, fontWeight: 700, marginTop: 6, color: valueColor || T.text, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 13, color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>}
    </div>
  );
}

// A row that opens something when tapped, keyboard included.
export function Row({ isDark, T, onOpen, children, title }) {
  const c = colors(isDark);
  const clickable = !!onOpen;
  return (
    <div role={clickable ? 'button' : undefined} tabIndex={clickable ? 0 : undefined} title={title}
      onClick={onOpen} onKeyDown={clickable ? (e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }) : undefined}
      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: c.row,
        border: `1px solid ${T.cardBorder}`, cursor: clickable ? 'pointer' : 'default', minWidth: 0, flexWrap: 'wrap' }}>
      {children}
    </div>
  );
}

export function Empty({ T, children }) {
  return <div style={{ fontSize: 14, color: T.textSub, padding: '10px 2px' }}>{children}</div>;
}
